/**
 * Email Template Constants
 *
 * Centralized constants for email templates to ensure consistency
 * across all email communications.
 */

import { SUPPORT_EMAIL, WEBSITE_URL } from "~shared/constants/domains";

// Company Information
export const EMAIL_CONSTANTS = {
  // Contact Information
  SUPPORT_EMAIL,
  COMPANY_URL: WEBSITE_URL,
  COMPANY_NAME: "Yield",
  COMPANY_ADDRESS: "Chicago, Illinois",

  // Expiration Settings
  INVITATION_EXPIRY_DAYS: 14,
  PASSWORD_RESET_EXPIRY_HOURS: 24,

  // Onboarding Links
  ONBOARDING_GUIDE_URL: "https://pioneer-dev-ai.notion.site/eip-for-new-users",
  SUPER_ADMIN_ONBOARDING_URL:
    "https://www.notion.so/pioneer-dev-ai/EIP-for-Elohi-Staff-27ed9b0e8a5f807693b3f31ba3a259d1/",
} as const;

// Typography
export const EMAIL_TYPOGRAPHY = {
  FONT_FAMILY:
    '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
} as const;

// Color Palette
export const EMAIL_COLORS = {
  // Background Colors
  BACKGROUND_LIGHT: "#f8fafc",
  BACKGROUND_WHITE: "#ffffff",
  BACKGROUND_GRAY: "#f6f9fc",

  // Text Colors
  TEXT_PRIMARY: "#333333",
  TEXT_SECONDARY: "#718096",
  TEXT_DARK: "#1a202c",
  TEXT_MUTED: "#4a5568",
  TEXT_GRAY: "#495057",
  TEXT_FOOTER: "#8898aa",

  // Brand Colors
  BRAND_PRIMARY: "#667eea",
  BRAND_SECONDARY: "#764ba2",
  BRAND_SUCCESS: "#10b981",
  BRAND_SUCCESS_DARK: "#059669",
  BRAND_WARNING: "#f59e0b",
  BRAND_WARNING_DARK: "#d97706",
  BRAND_INFO: "#007bff",

  // Status Colors
  SUCCESS_BG: "#d4edda",
  SUCCESS_BORDER: "#c3e6cb",
  SUCCESS_TEXT: "#155724",
  SUCCESS_LIGHT: "#f0fdf4",
  SUCCESS_TEXT_LIGHT: "#047857",
  SUCCESS_TEXT_LIGHTER: "#065f46",

  ERROR_BG: "#f8d7da",
  ERROR_BORDER: "#f5c6cb",
  ERROR_TEXT: "#721c24",

  WARNING_BG: "#fef3c7",
  WARNING_BORDER: "#f59e0b",
  WARNING_TEXT: "#92400e",

  INFO_BG: "#f8f9fa",
  INFO_BORDER: "#e9ecef",

  NOTICE_BG: "#fef5e7",
  NOTICE_BORDER: "#f6ad55",
  NOTICE_TEXT: "#744210",

  // Border Colors
  BORDER_LIGHT: "#e2e8f0",
} as const;

// Layout Dimensions
export const EMAIL_LAYOUT = {
  CONTAINER_MAX_WIDTH: "600px",
  BORDER_RADIUS_LARGE: "12px",
  BORDER_RADIUS_MEDIUM: "8px",
  BORDER_RADIUS_SMALL: "6px",

  // Spacing
  PADDING_SMALL: "20px",
  PADDING_MEDIUM: "24px",
  PADDING_LARGE: "40px",

  // Header Dimensions
  HEADER_PADDING: "40px 30px",

  // Content Dimensions
  CONTENT_PADDING: "40px 30px",
} as const;

// Gradient Styles
export const EMAIL_GRADIENTS = {
  PRIMARY: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  SUCCESS: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  WARNING: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  INFO: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
} as const;

// Font Sizes
export const EMAIL_FONT_SIZES = {
  HEADING_LARGE: "28px",
  HEADING_MEDIUM: "20px",
  HEADING_SMALL: "18px",
  TITLE: "16px",
  BODY: "16px",
  BODY_SMALL: "15px",
  CAPTION: "14px",
  FOOTNOTE: "13px",
} as const;

// Font Weights
export const EMAIL_FONT_WEIGHTS = {
  NORMAL: "400",
  MEDIUM: "500",
  SEMIBOLD: "600",
  BOLD: "bold",
} as const;

// Line Heights
export const EMAIL_LINE_HEIGHTS = {
  TIGHT: "1.2",
  NORMAL: "1.6",
  RELAXED: "1.8",
  HEADING: "20px",
  BODY: "22px",
  BODY_SMALL: "19px",
  CAPTION: "20px",
} as const;
